import React from 'react';
import { 
  FaCheckCircle, 
  FaDownload, 
  FaShareAlt, 
  FaCalendarAlt,
  FaUserFriends,
  FaTicketAlt,
  FaMapMarkerAlt,
  FaClock,
} from 'react-icons/fa';
import { useCurrency } from '../../context/CurrencyContext';
import { formatPriceSync } from '../../utils/currency';

const BookingComplete = ({
  lastBooking,
  selectedActivity,
  selectedOption,
  selectedDate,
  selectedTime,
  visitors,
  priceBreakdown,
  selectedCountry,
  resetBookingFlow,
}) => {
  const { currency } = useCurrency();
  const totalVisitors = visitors?.reduce((sum, v) => sum + v.count, 0) || 0;
  const bookingId = lastBooking?.bookingReference || `BK-${Math.random().toString(36).substr(2, 8).toUpperCase()}`;
  const amountPaid = lastBooking?.totalPrice ?? priceBreakdown?.total ?? 0;

  return (
    <div className="text-center py-8 sm:py-12 max-w-3xl mx-auto">
      <div className="relative inline-block mb-6 sm:mb-8">
        <div className="absolute inset-0 bg-emerald-400 rounded-full blur-2xl opacity-30" />
        <div className="relative bg-gradient-to-r from-emerald-500 to-green-600 w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center shadow-lg">
          <FaCheckCircle className="text-white text-4xl sm:text-5xl" />
        </div>
      </div>

      <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
        Booking confirmed
      </h2>
      <p className="text-slate-600 mb-8 sm:mb-10 max-w-xl mx-auto text-sm sm:text-base">
        Your adventure is booked. Confirmation details are below. Get ready for a great experience!
      </p>

      {lastBooking?.paymentMethod === "wallet" && (
        <div className="mb-6 mx-auto max-w-xl bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center justify-center gap-2 text-emerald-800">
          <FaCheckCircle className="text-emerald-600 flex-shrink-0" />
          <span className="font-medium">Paisa wallet se kat gaya. Amount deducted from wallet successfully.</span>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden mb-8 sm:mb-10">
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-5 sm:px-8 py-6 text-white">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="text-xl sm:text-2xl font-bold mb-1">#{bookingId}</h3>
              <p className="text-blue-100 text-sm">Status: <span className="font-semibold text-white">Confirmed</span></p>
            </div>
            <div className="text-right">
              <div className="text-2xl sm:text-3xl font-bold">{formatPriceSync(amountPaid, currency)}</div>
              <div className="text-blue-200 text-xs sm:text-sm">{lastBooking?.paymentMethod === "wallet" ? "Paid with wallet" : "Amount paid"}</div>
            </div>
          </div>
        </div>

        <div className="p-5 sm:p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-6 sm:mb-8">
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FaMapMarkerAlt className="text-blue-600" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs text-slate-500 uppercase tracking-wide">Destination</div>
                  <div className="font-semibold text-gray-900 truncate">{selectedCountry?.name}</div>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <FaTicketAlt className="text-emerald-600" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs text-slate-500 uppercase tracking-wide">Activity</div>
                  <div className="font-semibold text-gray-900 truncate">{selectedActivity?.name}</div>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FaCalendarAlt className="text-purple-600" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs text-slate-500 uppercase tracking-wide">Date & time</div>
                  <div className="font-semibold text-gray-900">{selectedDate} at {selectedTime}</div>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <FaUserFriends className="text-amber-600" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs text-slate-500 uppercase tracking-wide">Guests</div>
                  <div className="font-semibold text-gray-900">{totalVisitors}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h4 className="text-base font-bold text-gray-900 mb-4">Important info</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <div className="flex items-center gap-2 mb-2">
                  <FaClock className="text-blue-600 flex-shrink-0" />
                  <span className="font-medium text-gray-900">Check-in</span>
                </div>
                <p className="text-sm text-slate-600">
                  Arrive 30 minutes before the scheduled time at the activity location.
                </p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                <div className="flex items-center gap-2 mb-2">
                  <FaCheckCircle className="text-emerald-600 flex-shrink-0" />
                  <span className="font-medium text-gray-900">What to bring</span>
                </div>
                <p className="text-sm text-slate-600">
                  Valid ID, comfortable clothing, and your booking confirmation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          type="button"
          onClick={resetBookingFlow}
          className="px-6 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all flex items-center justify-center"
        >
          Book another activity
        </button>
        <button
          type="button"
          className="px-6 py-3.5 border-2 border-blue-500 text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-colors flex items-center justify-center"
        >
          <FaDownload className="mr-2" />
          Download receipt
        </button>
        <button
          type="button"
          className="px-6 py-3.5 border-2 border-slate-300 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 transition-colors flex items-center justify-center"
        >
          <FaShareAlt className="mr-2" />
          Share
        </button>
      </div>

      <p className="mt-8 text-sm text-slate-500">
        Need help? Contact support.
      </p>
    </div>
  );
};

export default BookingComplete;